import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import FormLabel from "../../Components/FormLabel";
import { useTranslation } from "react-i18next";
import {
  cancelAPIRequest,
  classNames,
  getErrorMessages,
  getWorkEndBounds,
  getWorkStartBounds,
  parseTimeString,
} from "../../Utils";
import ReactDatePicker from "react-datepicker";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { Button } from "../../Components/Button";
import { BANKS } from "../../Constants";
import {
  clearBusinessEmployee,
  requestCreateBusinessEmployee,
  requestGetBusinessEmployee,
  requestUpdateBusinessEmployee,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { Alert } from "../../Components/Alert";
import { CheckIcon } from "@heroicons/react/outline";
import {
  DEPENDENT_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  ID_TYPE_OPTIONS,
  LATE_DEDUCTION_TYPE_OPTIONS,
  MARRIAGE_STATUS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_PERIOD_OPTIONS,
  RISK_LEVEL_OPTIONS,
  SALARY_TYPE_OPTIONS,
  TAX_METHOD_OPTIONS,
} from "../../Constants/business";
import LateDeductionComponent from "../../Components/LateDeductionComponent";

const FEATURE_RELEASED = false;

const EmployeeCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const employeeIdParam = searchParams.get("id");

  const dispatch = useBusinessTransactionDispatch();
  const { user } = useAuthState();
  const {
    request,
    employee,
    getEmployeeLoading,
    getEmployeeError,
    newEmployee,
    newEmployeeLoading,
    newEmployeeError,
    updateEmployee,
    updateEmployeeLoading,
    updateEmployeeError,
  } = useBusinessTransactionState();

  const [showSuccessModal, setshowSuccessModal] = useState(false);

  const [formData, setformData] = useState({});
  const [errors, seterrors] = useState({});
  const [currentStep, setcurrentStep] = useState(0);

  const today = new Date();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const cleanedPhone = formData.phone?.replace(/\s/g, "");

  const steps = [
    { name: t("employees.modal.steps.personal"), section: "personalInfo" },
    { name: t("employees.modal.steps.identity"), section: "identity" },
    { name: t("employees.modal.steps.job"), section: "job" },
    { name: t("employees.salary.title"), section: "salary" },
  ];

  const workStartBounds = React.useMemo(
    () =>
      getWorkStartBounds(
        formData.standardWorkStartTime,
        formData.standardWorkEndTime,
        15
      ),
    [formData.standardWorkStartTime, formData.standardWorkEndTime]
  );

  const workEndBounds = React.useMemo(
    () =>
      getWorkEndBounds(
        formData.standardWorkStartTime,
        formData.standardWorkEndTime,
        15
      ),
    [formData.standardWorkStartTime, formData.standardWorkEndTime]
  );

  const useTranslatedOptions = (options, t) =>
    useMemo(
      () =>
        options.map((option) => ({
          label: t(option.label),
          value: option.value,
        })),
      [options, t]
    );

  const genderOptionsTranslated = useTranslatedOptions(GENDER_OPTIONS, t);
  const idTypeOptionsTranslated = useTranslatedOptions(ID_TYPE_OPTIONS, t);
  const employmentTypeOptionsTranslated = useTranslatedOptions(
    EMPLOYMENT_TYPE_OPTIONS,
    t
  );
  const employmentStatusOptionsTranslated = useTranslatedOptions(
    EMPLOYMENT_STATUS_OPTIONS,
    t
  );
  const salaryTypeOptionsTranslated = useTranslatedOptions(
    SALARY_TYPE_OPTIONS,
    t
  );
  const paymentMethodOptionsTranslated = useTranslatedOptions(
    PAYMENT_METHOD_OPTIONS,
    t
  );
  const paymentPeriodOptionsTranslated = useTranslatedOptions(
    PAYMENT_PERIOD_OPTIONS,
    t
  );
  const marriageStatusOptionsTranslated = useTranslatedOptions(
    MARRIAGE_STATUS_OPTIONS,
    t
  );
  const taxMethodOptionsTranslated = useTranslatedOptions(
    TAX_METHOD_OPTIONS,
    t
  );
  const riskLevelOptionsTranslated = useTranslatedOptions(
    RISK_LEVEL_OPTIONS,
    t
  );
  const lateDeductionTypeOptionsTranslated = useTranslatedOptions(
    LATE_DEDUCTION_TYPE_OPTIONS,
    t
  );

  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearBusinessEmployee(dispatch);
      setformData({});
      seterrors({});
      setcurrentStep(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    requestGetBusinessEmployee(dispatch, { id: employeeIdParam });
  }, [dispatch, employeeIdParam]);

  // Populate form when editing
  useEffect(() => {
    if (employee?.id) {
      setformData({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        gender:
          genderOptionsTranslated.find(
            (option) => option.value === employee.gender
          ) || null,
        marriageStatus:
          marriageStatusOptionsTranslated.find(
            (option) => option.value === employee.marriageStatus
          ) || null,
        dependent:
          DEPENDENT_OPTIONS.find(
            (option) => option.value === employee.dependent
          ) || null,
        birthDate: employee.birthDate ? new Date(employee.birthDate) : null,
        birthPlace: employee.birthPlace || "",
        email: employee.email || "",
        phone: employee.phone || "",
        address: employee.address || "",

        idType:
          idTypeOptionsTranslated.find(
            (option) => option.value === employee.idType
          ) || null,
        idNumber: employee.idNumber || "",
        npwp: employee.npwp || "",

        employeeId: employee.employeeID || "",
        employmentType:
          employmentTypeOptionsTranslated.find(
            (option) => option.value === employee.employmentType
          ) || null,
        employmentStatus:
          employmentStatusOptionsTranslated.find(
            (option) => option.value === employee.employmentStatus
          ) || null,
        riskLevel:
          riskLevelOptionsTranslated.find(
            (option) => option.value === employee.riskLevel
          ) || null,
        startDate: employee.startDate ? new Date(employee.startDate) : null,
        endDate: employee.endDate ? new Date(employee.endDate) : null,
        taxSubjectStartDate: employee.taxSubjectStartDate
          ? new Date(employee.taxSubjectStartDate)
          : null,
        taxSubjectEndDate: employee.taxSubjectEndDate
          ? new Date(employee.taxSubjectEndDate)
          : null,
        department: employee.department || "",
        position: employee.position || "",

        salaryType:
          salaryTypeOptionsTranslated.find(
            (option) => option.value === employee.salaryType
          ) || null,
        baseSalary: employee.baseSalary ? employee.baseSalary.toString() : "",
        taxMethod:
          taxMethodOptionsTranslated.find(
            (option) => option.value === employee.taxMethod
          ) || null,
        paymentMethod:
          paymentMethodOptionsTranslated.find(
            (option) => option.value === employee.paymentMethod
          ) || null,
        paymentPeriod:
          paymentPeriodOptionsTranslated.find(
            (option) => option.value === employee.paymentPeriod
          ) || null,

        bankName:
          BANKS.find(
            (bank) =>
              bank.value === employee.bankAccount?.bankName ||
              String(bank.id) === String(employee.bankAccount?.bankName)
          ) || null,
        bankBranch: employee.bankAccount?.bankBranch || "",
        accountNumber: employee.bankAccount?.accountNumber || "",
        accountHolderName: employee.bankAccount?.accountHolderName || "",
        minimumWorkHoursPerDay: employee.minimumWorkHoursPerDay || "",
        standardWorkStartTime: employee.standardWorkStartTime
          ? parseTimeString(employee.standardWorkStartTime)
          : null,
        standardWorkEndTime: employee.standardWorkEndTime
          ? parseTimeString(employee.standardWorkEndTime)
          : null,
        lateThresholdMinutes: employee.lateThresholdMinutes || "",
        earlyLeaveThresholdMinutes: employee.earlyLeaveThresholdMinutes || "",
        lateDeductionEnabled: employee.lateDeductionEnabled || false,
        lateDeductionType: employee.lateDeductionType
          ? lateDeductionTypeOptionsTranslated.find(
              (opt) => opt.value === employee.lateDeductionType
            )
          : null,
        lateDeductionRate: employee.lateDeductionRate?.toString() || "",
        lateDeductionMultiplier: employee.lateDeductionMultiplier || "",
        lateDeductionBasedOnSalary:
          employee.lateDeductionBasedOnSalary || false,
        maxLateDeductionPerDay: employee.maxLateDeductionPerDay || "",
      });
    }
  }, [
    employee,
    employmentStatusOptionsTranslated,
    employmentTypeOptionsTranslated,
    idTypeOptionsTranslated,
    salaryTypeOptionsTranslated,
    genderOptionsTranslated,
    paymentMethodOptionsTranslated,
    paymentPeriodOptionsTranslated,
    marriageStatusOptionsTranslated,
    taxMethodOptionsTranslated,
    riskLevelOptionsTranslated,
    lateDeductionTypeOptionsTranslated,
  ]);

  useEffect(() => {
    if (newEmployee?.id || updateEmployee?.id) {
      setshowSuccessModal(true);
    }
  }, [newEmployee?.id, updateEmployee?.id]);

  const handleChange = (field, value) => {
    setformData((prev) => {
      const updates = { [field]: value };

      if (field === "firstName" || field === "lastName") {
        const firstName = field === "firstName" ? value : prev.firstName;
        const lastName = field === "lastName" ? value : prev.lastName;

        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

        if (fullName) {
          updates.accountHolderName = fullName;
        }
      }

      return { ...prev, ...updates };
    });

    if (errors[field]) {
      seterrors((prev) => ({
        ...prev,
        [field]: "",
        ...(field === "firstName" || field === "lastName"
          ? { accountHolderName: "" }
          : {}),
      }));
    }
  };

  const getDefaultBirthDate = () => {
    return new Date(
      today.getFullYear() - 17,
      today.getMonth(),
      today.getDate()
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = "employees.errors.firstNameRequired";
    }
    if (!formData.gender) {
      newErrors.gender = "employees.errors.genderRequired";
    }
    if (!formData.marriageStatus) {
      newErrors.marriageStatus = "employees.errors.marriageStatusRequired";
    }
    if (formData.birthDate) {
      const birthDate = new Date(formData.birthDate);
      if (birthDate > today) {
        newErrors.birthDate = "employees.errors.futureDate";
      }
    }
    if (!formData.email?.trim()) {
      newErrors.email = "employees.errors.emailRequired";
    }
    if (formData.email) {
      const isEmailValid = emailRegex.test(formData.email);
      if (!isEmailValid) {
        newErrors.email = "employees.errors.emailInvalid";
      }
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = "employees.errors.phoneRequired";
    }
    if (formData.phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (!isPhoneValid) {
        newErrors.phone = "employees.errors.phoneInvalid";
      }
    }

    if (!formData.idType) {
      newErrors.idType = "employees.errors.idTypeRequired";
    }
    if (!formData.idNumber?.trim()) {
      newErrors.idNumber = "employees.errors.idNumberRequired";
    }
    if (formData.npwp) {
      const isNpwpValid =
        formData.npwp.length >= 15 && formData.npwp.length <= 16;
      if (!isNpwpValid) {
        newErrors.npwp = "employees.errors.npwpInvalid";
      }
    }

    if (!formData.employeeId?.trim()) {
      newErrors.employeeId = "employees.errors.employeeIdRequired";
    }
    if (!formData.employmentType) {
      newErrors.employmentType = "employees.errors.employmentTypeRequired";
    }
    if (!formData.employmentStatus) {
      newErrors.employmentStatus = "employees.errors.employmentStatusRequired";
    }
    if (!formData.startDate) {
      newErrors.startDate = "employees.errors.startDateRequired";
    }
    if (formData.startDate) {
      const startDate = new Date(formData.startDate);
      if (startDate > today) {
        newErrors.startDate = "employees.errors.futureDate";
      }
    }
    if (formData.minimumWorkHoursPerDay) {
      const value = parseFloat(formData.minimumWorkHoursPerDay);
      if (isNaN(value) || value < 1) {
        newErrors.minimumWorkHoursPerDay = "employees.errors.minimumWorkHours";
      }
    }

    if (!formData.salaryType) {
      newErrors.salaryType = "employees.errors.salaryTypeRequired";
    }
    if (!formData.baseSalary) {
      newErrors.baseSalary = "employees.errors.baseSalaryRequired";
    }
    if (!formData.taxMethod) {
      newErrors.taxMethod = "employees.errors.taxMethodRequired";
    }
    if (
      formData.taxSubjectStartDate &&
      formData.taxSubjectEndDate &&
      formData.taxSubjectEndDate < formData.taxSubjectStartDate
    ) {
      newErrors.taxSubjectEndDate = "employees.errors.taxSubjectEndBeforeStart";
    }
    if (!formData.paymentPeriod) {
      newErrors.paymentPeriod = "employees.errors.paymentPeriodRequired";
    }
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = "employees.errors.paymentMethodRequired";
    }
    if (formData.paymentMethod?.value === "BANK_TRANSFER") {
      if (!formData.bankName) {
        newErrors.bankName = "employees.errors.bankNameRequired";
      }
      if (!formData.bankBranch?.trim()) {
        newErrors.bankBranch = "employees.errors.bankBranchRequired";
      }
      if (!formData.accountNumber?.trim()) {
        newErrors.accountNumber = "employees.errors.accountNumberRequired";
      }
      if (!formData.accountHolderName?.trim()) {
        newErrors.accountHolderName =
          "employees.errors.accountHolderNameRequired";
      }
    }

    seterrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTiers = () => {
    const errors = [];
    const tiers = formData.lateDeductionTiers || [];

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];

      if (tier.fromMinutes >= tier.toMinutes) {
        errors.push(
          t("employees.lateDeduction.tiers.errors.endTimeGreaterThanStart", {
            tier: i + 1,
          })
        );
      }

      if (i > 0 && tier.fromMinutes <= tiers[i - 1].toMinutes) {
        errors.push(
          t(
            "employees.lateDeduction.tiers.errors.startGreaterThanPreviousEnd",
            {
              tier: i + 1,
            }
          )
        );
      }

      if (tier.deductionRate <= 0 || tier.deductionRate > 100) {
        errors.push(
          t("employees.lateDeduction.tiers.errors.deductionRateRange", {
            tier: i + 1,
          })
        );
      }
    }

    return errors;
  };

  const renderFormSection = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
            <div>
              <FormLabel
                htmlFor="firstName"
                label={t("employees.fields.firstName")}
                required
              />
              <input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName || ""}
                onChange={(e) => handleChange("firstName", e.target.value)}
                className={classNames(
                  errors.firstName
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
                placeholder={t("employees.placeholders.firstName")}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.firstName)}
                </p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="lastName"
                label={t("employees.fields.lastName")}
              />
              <input
                type="text"
                name="lastName"
                id="lastName"
                value={formData.lastName || ""}
                onChange={(e) => handleChange("lastName", e.target.value)}
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm"
                placeholder={t("employees.placeholders.lastName")}
              />
            </div>
            <div>
              <FormLabel
                htmlFor="gender"
                label={t("employees.fields.gender")}
                required
              />
              <Select
                id="gender"
                name="gender"
                className={classNames(
                  errors.gender
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 text-sm shadow-sm"
                )}
                classNamePrefix="select2-selection"
                options={genderOptionsTranslated}
                placeholder={t("employees.placeholders.gender")}
                value={formData.gender || null}
                onChange={(option) => handleChange("gender", option)}
                menuPortalTarget={document.body}
              />
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{t(errors.gender)}</p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="idType"
                label={t("employees.fields.marriageStatus")}
                required
              />
              <Select
                id="marriageStatus"
                name="marriageStatus"
                className={classNames(
                  errors.marriageStatus
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "text-sm mt-1 w-full shadow-sm"
                )}
                classNamePrefix="select2-selection"
                placeholder={t("employees.placeholders.marriageStatus")}
                options={marriageStatusOptionsTranslated}
                value={formData.marriageStatus || null}
                onChange={(option) => handleChange("marriageStatus", option)}
                menuPortalTarget={document.body}
              />
              {errors.marriageStatus && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.marriageStatus)}
                </p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="idType"
                label={t("employees.fields.dependent")}
              />
              <Select
                id="dependent"
                name="dependent"
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm mt-1 w-full rounded-md shadow-sm"
                classNamePrefix="select2-selection"
                placeholder={t("employees.placeholders.dependent")}
                isClearable
                options={DEPENDENT_OPTIONS}
                value={formData.dependent || null}
                onChange={(option) => handleChange("dependent", option)}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <FormLabel
                htmlFor="birthDate"
                label={t("employees.fields.birthDate")}
              />
              <div>
                <ReactDatePicker
                  className={classNames(
                    errors.birthDate
                      ? "border-red-500 border focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "block text-sm mt-1 w-full rounded-md shadow-sm"
                  )}
                  selected={formData.birthDate || getDefaultBirthDate() || null}
                  onChange={(date) => handleChange("birthDate", date)}
                  placeholderText={t("employees.placeholders.birthDate")}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  useShortMonthInDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={70}
                  maxDate={today}
                />
              </div>
              {errors.birthDate && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.birthDate)}
                </p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="birthPlace"
                label={t("employees.fields.birthPlace")}
              />
              <input
                type="text"
                name="birthPlace"
                id="birthPlace"
                value={formData.birthPlace || ""}
                onChange={(e) => handleChange("birthPlace", e.target.value)}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                placeholder={t("employees.placeholders.birthPlace")}
              />
            </div>
            <div>
              <FormLabel
                htmlFor="email"
                label={t("employees.fields.email")}
                required
              />
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email || ""}
                placeholder={t("employees.placeholders.email")}
                onChange={(e) => handleChange("email", e.target.value)}
                className={classNames(
                  errors.email
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
              />
              {errors.email && (
                <div className="mt-1 text-red-600 text-sm">
                  {t(errors.email)}
                </div>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="phone"
                label={t("employees.fields.phone")}
                required
              />
              <FormPhoneInput
                id="phone"
                name="phone"
                data-testid="phone"
                value={formData.phone || ""}
                onChange={(value) => handleChange("phone", value)}
                placeholder={t("employees.placeholders.phone")}
                className={classNames(
                  errors.phone
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
              />
              {errors.phone && (
                <div className="mt-1 text-red-600 text-sm">
                  {t(errors.phone)}
                </div>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="address"
                label={t("employees.fields.address")}
              />
              <textarea
                name="address"
                id="address"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                placeholder={t("employees.placeholders.address")}
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm py-2 px-3"
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
            <div>
              <FormLabel
                htmlFor="idType"
                label={t("employees.fields.idType")}
                required
              />
              <Select
                id="idType"
                name="idType"
                className={classNames(
                  errors.idType
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "text-sm mt-1 w-full"
                )}
                classNamePrefix="select2-selection"
                placeholder={t("employees.placeholders.idType")}
                options={idTypeOptionsTranslated}
                value={formData.idType || null}
                onChange={(option) => handleChange("idType", option)}
                menuPortalTarget={document.body}
              />
              {errors.idType && (
                <p className="text-red-500 text-sm mt-1">{t(errors.idType)}</p>
              )}
            </div>
            {formData.idType?.value === "KTP" ? (
              <div>
                <FormLabel
                  htmlFor="idNumber"
                  label={t("employees.fields.idNumber")}
                  required
                />
                <NumericFormat
                  type="text"
                  name="idNumber"
                  id="idNumber"
                  value={formData.idNumber || ""}
                  allowLeadingZeros
                  onChange={(e) => handleChange("idNumber", e.target.value)}
                  placeholder={t("employees.placeholders.idNumber")}
                  className={classNames(
                    errors.idNumber
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                  )}
                  isAllowed={({ value }) => value.length <= 16}
                  allowNegative={false}
                />
                {errors.idNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {t(errors.idNumber)}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <FormLabel
                  htmlFor="idNumber"
                  label={t("employees.fields.idNumber")}
                  required
                />
                <input
                  type="text"
                  name="idNumber"
                  id="idNumber"
                  value={formData.idNumber || ""}
                  placeholder={t("employees.placeholders.idNumber")}
                  onChange={(e) => handleChange("idNumber", e.target.value)}
                  className={classNames(
                    errors.idNumber
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                  )}
                  maxLength={9}
                />
                {errors.idNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {t(errors.idNumber)}
                  </p>
                )}
              </div>
            )}
            <div>
              <FormLabel htmlFor="npwp" label={t("employees.fields.npwp")} />
              <NumericFormat
                type="text"
                name="npwp"
                id="npwp"
                data-testid="npwp"
                allowLeadingZeros
                className={classNames(
                  errors.npwp
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
                placeholder={t("employees.placeholders.npwp")}
                displayType="input"
                value={formData.npwp || ""}
                onValueChange={(value) => {
                  handleChange("npwp", value.formattedValue);
                }}
                isAllowed={({ value }) => value.length <= 16}
                allowNegative={false}
              />
              {errors.npwp && (
                <div className="mt-1 text-red-600 text-sm">
                  {t(errors.npwp)}
                </div>
              )}
            </div>
          </div>
        );
      case 2:
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
              <div>
                <FormLabel
                  htmlFor="employeeId"
                  label={t("employees.fields.employeeId")}
                  required
                />
                <input
                  type="text"
                  name="employeeId"
                  id="employeeId"
                  value={formData.employeeId || ""}
                  onChange={(e) => handleChange("employeeId", e.target.value)}
                  placeholder={t("employees.placeholders.employeeId")}
                  className={classNames(
                    errors.employeeId
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                  )}
                />
                {errors.employeeId && (
                  <p className="text-red-500 text-sm mt-1">
                    {t(errors.employeeId)}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FormLabel
                    htmlFor="employmentType"
                    label={t("employees.fields.employmentType")}
                    required
                  />
                  <Select
                    id="employmentType"
                    name="employmentType"
                    className={classNames(
                      errors.employmentType
                        ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "text-sm mt-1 w-full"
                    )}
                    classNamePrefix="select2-selection"
                    options={employmentTypeOptionsTranslated}
                    value={formData.employmentType || null}
                    placeholder={t("employees.placeholders.employmentType")}
                    onChange={(option) =>
                      handleChange("employmentType", option)
                    }
                    menuPortalTarget={document.body}
                  />
                  {errors.employmentType && (
                    <p className="text-red-500 text-sm mt-1">
                      {t(errors.employmentType)}
                    </p>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="employmentStatus"
                    label={t("employees.fields.employmentStatus")}
                    required
                  />
                  <Select
                    id="employmentStatus"
                    name="employmentStatus"
                    className={classNames(
                      errors.employmentStatus
                        ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "text-sm mt-1 w-full"
                    )}
                    classNamePrefix="select2-selection"
                    options={employmentStatusOptionsTranslated}
                    value={formData.employmentStatus || null}
                    placeholder={t("employees.placeholders.employmentStatus")}
                    onChange={(option) =>
                      handleChange("employmentStatus", option)
                    }
                    menuPortalTarget={document.body}
                  />
                  {errors.employmentStatus && (
                    <p className="text-red-500 text-sm mt-1">
                      {t(errors.employmentStatus)}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <FormLabel
                  htmlFor="riskLevel"
                  label={t("employees.fields.riskLevel")}
                />
                <Select
                  id="riskLevel"
                  name="riskLevel"
                  className="text-sm w-full border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  classNamePrefix="select2-selection"
                  options={riskLevelOptionsTranslated}
                  value={formData.riskLevel || null}
                  placeholder={t("employees.placeholders.riskLevel")}
                  onChange={(option) => handleChange("riskLevel", option)}
                  menuPortalTarget={document.body}
                  isClearable
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FormLabel
                    htmlFor="startDate"
                    label={t("employees.fields.startDate")}
                    required
                  />
                  <ReactDatePicker
                    id="startDate"
                    selected={formData.startDate || null}
                    onChange={(date) => handleChange("startDate", date)}
                    maxDate={today}
                    placeholderText={t("employees.placeholders.startDate")}
                    className={classNames(
                      errors.startDate
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "block text-sm mt-1 w-full rounded-md shadow-sm"
                    )}
                    dateFormat="dd/MM/yyyy"
                    showMonthDropdown
                    useShortMonthInDropdown
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={70}
                  />
                  {errors.startDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {t(errors.startDate)}
                    </p>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="endDate"
                    label={t("employees.fields.endDate")}
                  />
                  <div className="relative flex mt-1">
                    <ReactDatePicker
                      id="endDate"
                      selected={formData.endDate || null}
                      onChange={(date) => handleChange("endDate", date)}
                      placeholderText={t("employees.placeholders.endDate")}
                      className="block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      dateFormat="dd/MM/yyyy"
                      showMonthDropdown
                      useShortMonthInDropdown
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={70}
                      minDate={
                        formData.startDate
                          ? new Date(formData.startDate.getTime() + 86400000)
                          : today
                      }
                    />
                    {formData.endDate && (
                      <button
                        type="button"
                        onClick={() => handleChange("endDate", null)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:text-gray-500 data-[disabled]:opacity-50"
                      >
                        <span className="sr-only">{t("common.clear")}</span>
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FormLabel
                    htmlFor="department"
                    label={t("employees.fields.department")}
                  />
                  <input
                    type="text"
                    name="department"
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleChange("department", e.target.value)}
                    placeholder={t("employees.placeholders.department")}
                    className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <FormLabel
                    htmlFor="position"
                    label={t("employees.fields.position")}
                  />
                  <input
                    type="text"
                    name="position"
                    id="position"
                    value={formData.position || ""}
                    onChange={(e) => handleChange("position", e.target.value)}
                    placeholder={t("employees.placeholders.position")}
                    className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
              <div>
                <FormLabel
                  htmlFor="minimumWorkHoursPerDay"
                  label={t("employees.fields.minimumWorkHoursPerDay")}
                />
                <NumericFormat
                  type="text"
                  name="minimumWorkHoursPerDay"
                  id="minimumWorkHoursPerDay"
                  value={formData.minimumWorkHoursPerDay || ""}
                  onChange={(e) =>
                    handleChange("minimumWorkHoursPerDay", e.target.value)
                  }
                  placeholder={t(
                    "employees.placeholders.minimumWorkHoursPerDay"
                  )}
                  isAllowed={({ value }) => value.length <= 3}
                  allowNegative={false}
                  className={classNames(
                    errors.minimumWorkHoursPerDay
                      ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                    "block rounded-md shadow-sm text-sm mt-1 w-full"
                  )}
                />
                {errors.minimumWorkHoursPerDay && (
                  <p className="text-red-500 text-sm mt-1">
                    {t(errors.minimumWorkHoursPerDay)}
                  </p>
                )}
              </div>
              <div>
                <FormLabel
                  htmlFor="standardWorkStartTime"
                  label={t("employees.fields.standardWorkStartTime")}
                />
                <div className="relative flex mt-1">
                  <ReactDatePicker
                    id="standardWorkStartTime"
                    selected={formData.standardWorkStartTime || null}
                    onChange={(date) =>
                      handleChange("standardWorkStartTime", date)
                    }
                    placeholderText={t(
                      "employees.placeholders.standardWorkStartTime"
                    )}
                    className="block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    minTime={workStartBounds.minTime}
                    maxTime={workStartBounds.maxTime}
                  />
                  {formData.standardWorkStartTime && (
                    <button
                      type="button"
                      onClick={() =>
                        handleChange("standardWorkStartTime", null)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:text-gray-500 data-[disabled]:opacity-50"
                    >
                      <span className="sr-only">{t("common.clear")}</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div>
                <FormLabel
                  htmlFor="standardWorkEndTime"
                  label={t("employees.fields.standardWorkEndTime")}
                />
                <div className="relative flex mt-1">
                  <ReactDatePicker
                    id="standardWorkEndTime"
                    selected={formData.standardWorkEndTime || null}
                    onChange={(date) =>
                      handleChange("standardWorkEndTime", date)
                    }
                    placeholderText={t(
                      "employees.placeholders.standardWorkEndTime"
                    )}
                    className="block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    minTime={workEndBounds.minTime}
                    maxTime={workEndBounds.maxTime}
                  />
                  {formData.standardWorkEndTime && (
                    <button
                      type="button"
                      onClick={() => handleChange("standardWorkEndTime", null)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:text-gray-500 data-[disabled]:opacity-50"
                    >
                      <span className="sr-only">{t("common.clear")}</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              {FEATURE_RELEASED && (
                <>
                  <div>
                    <FormLabel
                      htmlFor="lateThresholdMinutes"
                      label={t("employees.fields.lateThresholdMinutes")}
                    />
                    <NumericFormat
                      type="text"
                      name="lateThresholdMinutes"
                      id="lateThresholdMinutes"
                      value={formData.lateThresholdMinutes || ""}
                      onChange={(e) =>
                        handleChange("lateThresholdMinutes", e.target.value)
                      }
                      placeholder={t(
                        "employees.placeholders.lateThresholdMinutes"
                      )}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm"
                      isAllowed={({ value }) => value >= 0 && value <= 1440}
                    />
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="earlyLeaveThresholdMinutes"
                      label={t("employees.fields.earlyLeaveThresholdMinutes")}
                    />
                    <NumericFormat
                      type="text"
                      name="earlyLeaveThresholdMinutes"
                      id="earlyLeaveThresholdMinutes"
                      value={formData.earlyLeaveThresholdMinutes || ""}
                      onChange={(e) =>
                        handleChange(
                          "earlyLeaveThresholdMinutes",
                          e.target.value
                        )
                      }
                      placeholder={t(
                        "employees.placeholders.earlyLeaveThresholdMinutes"
                      )}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm"
                      isAllowed={({ value }) => value >= 0 && value <= 1440}
                    />
                  </div>
                </>
              )}
            </div>
            {FEATURE_RELEASED && (
              <div className="gap-6 p-4">
                <LateDeductionComponent
                  formData={formData}
                  handleChange={handleChange}
                  options={LATE_DEDUCTION_TYPE_OPTIONS}
                  validateTiers={validateTiers}
                />
              </div>
            )}
          </>
        );
      case 3:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
            <div>
              <FormLabel
                htmlFor="salaryType"
                label={t("employees.fields.salaryType")}
                required
              />
              <Select
                id="salaryType"
                name="salaryType"
                className={classNames(
                  errors.salaryType
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "text-sm mt-1 w-full"
                )}
                classNamePrefix="select2-selection"
                options={salaryTypeOptionsTranslated}
                value={formData.salaryType || null}
                placeholder={t("employees.placeholders.salaryType")}
                onChange={(option) => handleChange("salaryType", option)}
                menuPortalTarget={document.body}
              />
              {errors.salaryType && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.salaryType)}
                </p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="baseSalary"
                label={t("employees.fields.baseSalary")}
                required
              />
              <NumericFormat
                id="baseSalary"
                name="baseSalary"
                value={formData.baseSalary || ""}
                onValueChange={(value) =>
                  handleChange("baseSalary", value.floatValue)
                }
                placeholder={t("employees.placeholders.baseSalary")}
                thousandSeparator="."
                decimalSeparator=","
                displayType="input"
                prefix={"Rp"}
                className={classNames(
                  errors.baseSalary
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
              />
              {errors.baseSalary && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.baseSalary)}
                </p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="taxMethod"
                label={t("employees.fields.taxMethod")}
                required
              />
              <Select
                id="taxMethod"
                name="taxMethod"
                className={classNames(
                  errors.taxMethod
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "text-sm mt-1 w-full"
                )}
                classNamePrefix="select2-selection"
                options={taxMethodOptionsTranslated}
                value={formData.taxMethod || null}
                placeholder={t("employees.placeholders.taxMethod")}
                onChange={(option) => handleChange("taxMethod", option)}
                menuPortalTarget={document.body}
              />
              {errors.taxMethod && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.taxMethod)}
                </p>
              )}
            </div>
            <div className="sm:col-span-2">
              <p className="text-sm font-medium text-gray-900">
                {t("employees.fields.taxSubjectPeriod")}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {t("employees.fields.taxSubjectPeriodHelp")}
              </p>
            </div>
            <div>
              <FormLabel
                htmlFor="taxSubjectStartDate"
                label={t("employees.fields.taxSubjectStartDate")}
              />
              <ReactDatePicker
                id="taxSubjectStartDate"
                selected={formData.taxSubjectStartDate || null}
                onChange={(date) => handleChange("taxSubjectStartDate", date)}
                placeholderText={t("employees.placeholders.taxSubjectStartDate")}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                dateFormat="dd/MM/yyyy"
                isClearable
                showMonthDropdown
                useShortMonthInDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={70}
              />
            </div>
            <div>
              <FormLabel
                htmlFor="taxSubjectEndDate"
                label={t("employees.fields.taxSubjectEndDate")}
              />
              <ReactDatePicker
                id="taxSubjectEndDate"
                selected={formData.taxSubjectEndDate || null}
                onChange={(date) => handleChange("taxSubjectEndDate", date)}
                placeholderText={t("employees.placeholders.taxSubjectEndDate")}
                className={classNames(
                  errors.taxSubjectEndDate
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
                dateFormat="dd/MM/yyyy"
                isClearable
                showMonthDropdown
                useShortMonthInDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={70}
                minDate={formData.taxSubjectStartDate || undefined}
              />
              {errors.taxSubjectEndDate && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.taxSubjectEndDate)}
                </p>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="paymentMethod"
                label={t("employees.fields.paymentMethod")}
                required
              />
              <Select
                id="paymentMethod"
                name="paymentMethod"
                className={classNames(
                  errors.paymentMethod
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "text-sm mt-1 w-full"
                )}
                classNamePrefix="select2-selection"
                options={paymentMethodOptionsTranslated}
                value={formData.paymentMethod || null}
                onChange={(option) => handleChange("paymentMethod", option)}
                placeholder={t("employees.placeholders.paymentMethod")}
                menuPortalTarget={document.body}
              />
              {errors.paymentMethod && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.paymentMethod)}
                </p>
              )}
            </div>
            {formData.paymentMethod?.value === "BANK_TRANSFER" && (
              <>
                <div>
                  <FormLabel
                    htmlFor="bankName"
                    label={t("employees.fields.bankName")}
                    required
                  />
                  <Select
                    id="bankName"
                    name="bankName"
                    className={classNames(
                      errors.bankName
                        ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "text-sm mt-1 w-full"
                    )}
                    classNamePrefix="select2-selection"
                    options={BANKS.map((option) => ({
                      label: `${option.shortLabel} (${option.code}) - ${option.abbreviation}`,
                      value: option.value,
                    }))}
                    value={formData.bankName || null}
                    onChange={(option) => handleChange("bankName", option)}
                    placeholder={t("employees.placeholders.bankName")}
                    menuPortalTarget={document.body}
                  />
                  {errors.bankName && (
                    <p className="text-red-500 text-sm mt-1">
                      {t(errors.bankName)}
                    </p>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="bankBranch"
                    label={t("employees.fields.bankBranch")}
                    required
                  />
                  <input
                    type="text"
                    name="bankBranch"
                    id="bankBranch"
                    value={formData.bankBranch}
                    onChange={(e) => handleChange("bankBranch", e.target.value)}
                    placeholder={t("employees.placeholders.bankBranch")}
                    className={classNames(
                      errors.bankBranch
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {errors.bankBranch && (
                    <p className="text-red-500 text-sm mt-1">
                      {t(errors.bankBranch)}
                    </p>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="accountNumber"
                    label={t("employees.fields.accountNumber")}
                    required
                  />
                  <NumericFormat
                    type="text"
                    name="accountNumber"
                    id="accountNumber"
                    allowLeadingZeros
                    className={classNames(
                      errors.accountNumber
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                    placeholder={t("employees.placeholders.accountNumber")}
                    displayType="input"
                    value={formData.accountNumber || ""}
                    onValueChange={(value) => {
                      handleChange("accountNumber", value.formattedValue);
                    }}
                    allowNegative={false}
                  />
                  {errors.accountNumber && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(errors.accountNumber)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel
                    htmlFor="accountHolderName"
                    label={t("employees.fields.accountHolderName")}
                    required
                  />
                  <input
                    type="text"
                    name="accountHolderName"
                    id="accountHolderName"
                    value={formData.accountHolderName || ""}
                    onChange={(e) =>
                      handleChange("accountHolderName", e.target.value)
                    }
                    placeholder={t("employees.placeholders.accountHolderName")}
                    className={classNames(
                      errors.accountHolderName
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {errors.accountHolderName && (
                    <p className="text-red-500 text-sm mt-1">
                      {t(errors.accountHolderName)}
                    </p>
                  )}
                </div>
              </>
            )}
            <div>
              <FormLabel
                htmlFor="paymentPeriod"
                label={t("employees.fields.paymentPeriod")}
                required
              />
              <Select
                id="paymentPeriod"
                name="paymentPeriod"
                className={classNames(
                  errors.paymentPeriod
                    ? "border-red-500 rounded-md border focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "text-sm mt-1 w-full"
                )}
                classNamePrefix="select2-selection"
                options={paymentPeriodOptionsTranslated}
                value={formData.paymentPeriod || null}
                onChange={(option) => handleChange("paymentPeriod", option)}
                placeholder={t("employees.placeholders.paymentPeriod")}
                menuPortalTarget={document.body}
              />
              {errors.paymentPeriod && (
                <p className="text-red-500 text-sm mt-1">
                  {t(errors.paymentPeriod)}
                </p>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleSubmit = () => {
    const input = {
      businessID: user.mainBusiness.id,
      firstName: formData.firstName,
      lastName: formData.lastName || null,
      gender: formData.gender?.value,
      marriageStatus: formData.marriageStatus?.value,
      dependent: formData.dependent?.value || null,
      birthDate: formData.birthDate,
      birthPlace: formData.birthPlace,
      email: formData.email,
      phone: cleanedPhone,
      address: formData.address,
      idType: formData.idType?.value,
      idNumber: formData.idNumber,
      npwp: formData.npwp || null,
      employeeID: formData.employeeId,
      riskLevel: formData.riskLevel?.value || null,
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      employmentStatus: formData.employmentStatus?.value,
      employmentType: formData.employmentType?.value,
      department: formData.department || null,
      position: formData.position || null,
      salaryType: formData.salaryType?.value,
      baseSalary: formData.baseSalary || null,
      taxMethod: formData.taxMethod?.value,
      taxSubjectStartDate: formData.taxSubjectStartDate || null,
      taxSubjectEndDate: formData.taxSubjectEndDate || null,
      paymentMethod: formData.paymentMethod?.value,
      paymentPeriod: formData.paymentPeriod?.value,
      bankAccount:
        formData.paymentMethod?.value === "BANK_TRANSFER"
          ? {
              bankName: formData.bankName?.value,
              bankBranch: formData.bankBranch,
              accountNumber: formData.accountNumber,
              accountHolderName: formData.accountHolderName,
            }
          : null,
      minimumWorkHoursPerDay: formData.minimumWorkHoursPerDay
        ? parseFloat(formData.minimumWorkHoursPerDay)
        : null,
      standardWorkStartTime: formData.standardWorkStartTime
        ? new Date(formData.standardWorkStartTime).toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        : null,
      standardWorkEndTime: formData.standardWorkEndTime
        ? new Date(formData.standardWorkEndTime).toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        : null,
      lateThresholdMinutes: formData.lateThresholdMinutes
        ? parseInt(formData.lateThresholdMinutes)
        : null,
      earlyLeaveThresholdMinutes: formData.earlyLeaveThresholdMinutes
        ? parseInt(formData.earlyLeaveThresholdMinutes)
        : null,
      lateDeductionEnabled: formData.lateDeductionEnabled || false,
      lateDeductionType: formData.lateDeductionType?.value || null,
      lateDeductionRate: formData.lateDeductionRate || null,
      lateDeductionMultiplier: formData.lateDeductionMultiplier
        ? parseFloat(formData.lateDeductionMultiplier)
        : null,
      lateDeductionBasedOnSalary: formData.lateDeductionBasedOnSalary || false,
      maxLateDeductionPerDay: formData.maxLateDeductionPerDay
        ? parseFloat(formData.maxLateDeductionPerDay)
        : null,
    };

    if (employeeIdParam === employee?.id) {
      input.id = employee.id;
      requestUpdateBusinessEmployee(dispatch, { input });
    } else {
      requestCreateBusinessEmployee(dispatch, { input });
    }
  };

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      if (validateForm() && validateTiers().length === 0) {
        handleSubmit();
      }
    } else {
      setcurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setcurrentStep(currentStep - 1);
    }
  };

  const hasStepErrors = (section) => {
    switch (section) {
      case "personalInfo":
        return [
          "firstName",
          "gender",
          "marriageStatus",
          "birthDate",
          "birthPlace",
          "futureDate",
          "email",
          "phone",
        ].some((field) => errors[field]);
      case "identity":
        return ["idType", "idNumber", "npwp"].some((field) => errors[field]);
      case "job":
        const tierErrors =
          formData.lateDeductionEnabled &&
          formData.lateDeductionType?.value === "TIERED"
            ? validateTiers()
            : [];

        return [
          "employeeId",
          "employmentType",
          "employmentStatus",
          "startDate",
          "minimumWorkHoursPerDay",
        ].some((field) => errors[field] || tierErrors.length > 0);
      case "salary":
        return [
          "salaryType",
          "baseSalary",
          "taxMethod",
          "taxSubjectEndDate",
          "paymentMethod",
          "paymentPeriod",
          "bankName",
          "bankBranch",
          "accountNumber",
          "accountHolderName",
        ].some((field) => errors[field]);
      default:
        return false;
    }
  };

  const validateStepFields = (section) => {
    switch (section) {
      case "personalInfo":
        return (
          formData.firstName?.trim() &&
          formData.gender &&
          formData.email?.trim() &&
          emailRegex.test(formData.email) &&
          formData.phone?.trim() &&
          cleanedPhone?.length >= 10 &&
          (!formData.birthDate || formData.birthDate <= today)
        );

      case "identity":
        return (
          formData.idType &&
          formData.idNumber?.trim() &&
          (!formData.npwp ||
            (formData.npwp.length >= 15 && formData.npwp.length <= 16))
        );

      case "job":
        const tierErrors =
          formData.lateDeductionEnabled &&
          formData.lateDeductionType?.value === "TIERED"
            ? validateTiers()
            : [];

        const minHoursValid =
          !formData.minimumWorkHoursPerDay ||
          (!isNaN(parseFloat(formData.minimumWorkHoursPerDay)) &&
            parseFloat(formData.minimumWorkHoursPerDay) >= 1);

        return (
          formData.employeeId?.trim() &&
          formData.employmentType &&
          formData.employmentStatus &&
          formData.startDate &&
          formData.startDate <= today &&
          tierErrors.length === 0 &&
          minHoursValid
        );

      case "salary":
        const bankTransferValid =
          formData.paymentMethod?.value === "BANK_TRANSFER"
            ? formData.bankName &&
              formData.bankBranch?.trim() &&
              formData.accountNumber?.trim() &&
              formData.accountHolderName?.trim()
            : true;

        return (
          formData.salaryType &&
          formData.baseSalary &&
          formData.taxMethod &&
          formData.paymentMethod &&
          formData.paymentPeriod &&
          bankTransferValid
        );

      default:
        return false;
    }
  };

  const navigateToAccounting = () => {
    setshowSuccessModal(false);
    if (!newEmployeeError || !updateEmployeeError) {
      if (newEmployee.id || updateEmployee.id) {
        navigate("/v2/accounting/employees");
        clearBusinessEmployee(dispatch);
      }
    }
  };

  const StepSkeleton = () => (
    <nav aria-label="Progress" className=" animate-pulse w-full px-4 py-6">
      <ol className="flex flex-col sm:flex-row sm:justify-between w-full space-y-4 sm:space-y-0">
        {[...Array(4)].map((_, index) => (
          <li
            key={index}
            className={`relative ${index !== 3 ? "sm:pr-8 sm:flex-1" : ""}`}
          >
            <div className="flex sm:flex-col">
              <div className="relative">
                <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center" />
                {index !== 3 && (
                  <>
                    {/* Mobile connector */}
                    <div className="sm:hidden absolute left-4 top-8 w-0.5 h-6 bg-gray-200" />
                    {/* Desktop connector */}
                    <div className="hidden sm:block absolute top-4 left-8 h-0.5 w-full bg-gray-200" />
                  </>
                )}
              </div>
              <div className="ml-4 sm:ml-0 mt-2">
                <div className="h-4 w-20 bg-gray-200 rounded-md" />
              </div>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );

  const InputGroupSkeleton = () => (
    <div className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[...Array(6)].map((_, index) => (
          <div key={index}>
            <div className="h-4 w-24 bg-gray-200 rounded-md mb-2" />
            <div className="h-10 bg-gray-200 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );

  if (employeeIdParam && getEmployeeError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={getEmployeeError?.message}
          errors={getEmployeeError?.errors}
        />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-sm divide-y divide-gray-200">
      {(newEmployee || updateEmployee) && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description={
            updateEmployee?.id
              ? "employees.notifications.updateSuccess"
              : "employees.notifications.addSuccess"
          }
          type="SUCCESS"
          onConfirm={navigateToAccounting}
        />
      )}
      {getEmployeeLoading ? (
        <StepSkeleton />
      ) : (
        <nav aria-label="Progress" className="w-full px-4 py-6">
          <ol className="flex flex-col sm:flex-row sm:justify-between w-full space-y-4 sm:space-y-0">
            {steps.map((step, index) => {
              const hasError = hasStepErrors(step.section);
              const isCompleted = index < currentStep;
              const isActive = index === currentStep;
              const isValid = validateStepFields(step.section);

              return (
                <li
                  key={step.name}
                  className={`relative ${
                    index !== steps.length - 1 ? "sm:pr-8 sm:flex-1" : ""
                  }`}
                >
                  <div className="flex sm:flex-col">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setcurrentStep(index)}
                        className={`${
                          hasError
                            ? "bg-red-500"
                            : isActive
                            ? "bg-white border-2 border-blue-600"
                            : isCompleted && isValid
                            ? "bg-blue-600"
                            : "bg-gray-200"
                        } rounded-full h-8 w-8 flex items-center justify-center group relative flex-shrink-0`}
                      >
                        <span
                          className={`text-sm font-medium ${
                            hasError
                              ? "text-white"
                              : isActive
                              ? "text-blue-600"
                              : isCompleted && isValid
                              ? "text-white"
                              : "text-gray-500"
                          }`}
                        >
                          {hasError ? (
                            "!"
                          ) : isCompleted && isValid ? (
                            <CheckIcon aria-hidden="true" className="h-5 w-5" />
                          ) : (
                            index + 1
                          )}
                        </span>
                      </button>

                      {/* Mobile: Vertical connector line */}
                      {index !== steps.length - 1 && (
                        <div
                          className={`sm:hidden absolute left-4 top-8 w-0.5 h-6 ${
                            hasError
                              ? "bg-red-500"
                              : isCompleted && isValid
                              ? "bg-blue-600"
                              : "bg-gray-200"
                          }`}
                        />
                      )}

                      {/* Desktop: Horizontal connector line */}
                      {index !== steps.length - 1 && (
                        <div
                          className={`hidden sm:block absolute top-4 left-8 h-0.5 w-full ${
                            hasError
                              ? "bg-red-500"
                              : isCompleted && isValid
                              ? "bg-blue-600"
                              : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>
                    <div className="ml-4 sm:ml-0 mt-0.5">
                      <span
                        className={`text-sm font-medium ${
                          hasError
                            ? "text-red-500"
                            : isActive || (isCompleted && isValid)
                            ? "text-blue-600"
                            : "text-gray-500"
                        }`}
                      >
                        {step.name}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      {(newEmployeeError || updateEmployeeError) && (
        <div className="p-4">
          <Alert
            title="Error"
            type="error"
            className=""
            message={
              t(getErrorMessages(newEmployeeError || updateEmployeeError)) ||
              "Error create employee."
            }
            errors={newEmployeeError?.errors || updateEmployeeError?.errors}
          />
        </div>
      )}
      {getEmployeeLoading ? <InputGroupSkeleton /> : renderFormSection()}
      <div className="flex justify-between bg-gray-50 rounded-b-md sm:rounded-b-lg p-4">
        <Button
          type="button"
          title={t("common.previous")}
          onClick={handlePrevious}
          disabled={
            currentStep === 0 || newEmployeeLoading || getEmployeeLoading
          }
          loading={newEmployeeLoading || getEmployeeLoading}
          outline
        />
        <Button
          title={
            currentStep === steps.length - 1
              ? t("common.save")
              : t("common.next")
          }
          type="button"
          loading={
            newEmployeeLoading || getEmployeeLoading || updateEmployeeLoading
          }
          disabled={
            newEmployeeLoading || getEmployeeLoading || updateEmployeeLoading
          }
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

export default EmployeeCreate;
